import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Clock, Heart, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';

const HeroSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen hero-gradient overflow-hidden pt-24 pb-16"
    >
      {/* Floating decorative elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute top-32 left-[10%] w-16 h-16 rounded-full bg-[var(--vp-pink)]/20 float-animation"
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-48 right-[15%] w-12 h-12 rounded-full bg-[var(--vp-mint)]/30 float-animation-slow"
          animate={{ y: [0, -15, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-32 left-[20%] w-8 h-8 rounded-full bg-[var(--vp-sky)]/25"
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-[40%] right-[8%] w-20 h-20 rounded-2xl border-2 border-[var(--vp-line)] bg-white/50 rotate-12"
          animate={{ rotate: [12, 18, 12] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[80vh]">
          {/* Left Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-left"
          >
            <motion.div variants={itemVariants} className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--vp-sand)] rounded-full text-sm font-medium text-[var(--vp-ink)]">
                <Sparkles className="w-4 h-4 text-[var(--vp-pink)]" />
                Partnerská výzva již v provozu!
              </span>
            </motion.div>

            <motion.h1
              variants={itemVariants}
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[var(--vp-ink)] mb-6"
              style={{ lineHeight: '1.1' }}
            >
              Výzva přijata.
              <span className="block text-[var(--vp-pink)]">
                Rozhybte vztah.
              </span>
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="text-lg text-[var(--vp-ink)]/70 mb-8 max-w-lg"
            >
              Digitální hry a výzvy pro páry i nezapomenutelné rozlučky.
              14 dní společných momentů, které rozhybou autopilota.
            </motion.p>

            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4 mb-10"
            >
              <Button
                data-testid="hero-primary-cta-button"
                onClick={() => scrollToSection('#pricing')}
                className="btn-primary px-8 py-4 text-base rounded-full"
              >
                Koupit za 379 Kč
              </Button>
              <Button
                data-testid="hero-secondary-cta-button"
                onClick={() => scrollToSection('#how-it-works')}
                variant="outline"
                className="px-8 py-4 text-base rounded-full border-[var(--vp-line)] hover:bg-[var(--vp-sand)]"
              >
                Jak to funguje
              </Button>
            </motion.div>

            {/* Trust row */}
            <motion.div
              variants={itemVariants}
              className="flex flex-wrap gap-6"
            >
              <div className="trust-item">
                <Clock className="w-4 h-4 text-[var(--vp-pink)]" />
                <span>Okamžitě v mobilu</span>
              </div>
              <div className="trust-item">
                <Heart className="w-4 h-4 text-[var(--vp-pink)]" />
                <span>Pro 2 osoby</span>
              </div>
              <div className="trust-item">
                <MessageCircle className="w-4 h-4 text-[var(--vp-pink)]" />
                <span>14 dní SMS</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Preview Cards */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              {/* Main card */}
              <motion.div
                className="bg-white rounded-3xl p-6 shadow-[var(--shadow-lift)] border border-[var(--vp-line)]"
                whileHover={{ y: -5 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[var(--vp-ink)] flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-[var(--vp-ink)]">Výzva přijata</p>
                    <p className="text-sm text-[var(--vp-ink)]/60">Denní výzva</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="message-card message-card-left">
                    <p className="text-sm">
                      📱 Dnešní výzva: Napiš partnerovi 3 věci, za které jsi mu/jí dnes vděčný/á.
                    </p>
                  </div>
                  <div className="message-card message-card-right">
                    <p className="text-sm">
                      Hotovo! 💜 Bylo to krásné.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Floating mini cards */}
              <motion.div
                className="absolute -top-4 -right-4 bg-[var(--vp-pink)] text-white px-4 py-2 rounded-full text-sm font-semibold"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                379 Kč
              </motion.div>

              <motion.div
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-lg border border-[var(--vp-line)]"
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
              >
                <p className="text-xs text-[var(--vp-ink)]/60 mb-1">Den 7/14</p>
                <div className="flex gap-1">
                  {[...Array(7)].map((_, i) => (
                    <div key={i} className="w-2 h-2 rounded-full bg-[var(--vp-mint)]" />
                  ))}
                  {[...Array(7)].map((_, i) => (
                    <div key={i} className="w-2 h-2 rounded-full bg-[var(--vp-line)]" />
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
